import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson, authHeader } from '@/lib/api';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LOGO_URL } from '@/data/mockData';
import {
  ArrowLeft, Loader2, RefreshCw,
  ShoppingCart, Coins, FolderPlus, PlayCircle, Truck, MessageSquare, Sparkles,
} from 'lucide-react';

const ICON_BY_TYPE = {
  purchase: ShoppingCart,
  credit_transaction: Coins,
  project_created: FolderPlus,
  production_started: PlayCircle,
  delivery: Truck,
  delivered: Truck,
};

const ACCENT_BY_TYPE = {
  purchase: 'bg-emerald-50 text-emerald-600',
  credit_transaction: 'bg-gray-100 text-gray-500',
  project_created: 'bg-violet-50 text-violet-600',
  production_started: 'bg-amber-50 text-amber-600',
  delivery: 'bg-emerald-50 text-emerald-600',
  delivered: 'bg-emerald-50 text-emerald-600',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return `Hoy · ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleString('es-ES', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function groupByDay(items) {
  const groups = {};
  items.forEach((it) => {
    const k = (it.at || '').slice(0, 10) || 'sin-fecha';
    groups[k] = groups[k] || [];
    groups[k].push(it);
  });
  return Object.entries(groups).sort(([a], [b]) => (a < b ? 1 : -1));
}

export default function HistoryView({ onClose }) {
  const { session } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);
    const { ok, data } = await xhrJson(
      'GET', `${API}/api/history/me`, null, authHeader(session),
    );
    if (ok) {
      setItems(data?.items || []);
    } else {
      setError(data?.detail || 'No pudimos cargar tu historial.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.access_token]);

  const groups = groupByDay(items);

  return (
    <div className="flex flex-col h-[100dvh] md:h-full relative" data-testid="history-view">
      <div className="absolute top-0 left-0 right-0 z-10 px-4 md:px-6 pt-4 pointer-events-none">
        <div className="flex justify-center pl-12 md:pl-0 pointer-events-auto">
          <div className="glass-pill flex items-center gap-2.5 px-2 py-1.5 rounded-full">
            <button
              data-testid="history-back"
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-black/[0.04] transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <span className="text-[13px] font-medium text-gray-900 font-['Outfit'] pr-3">Historial</span>
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1 min-h-0 pt-16">
        <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-4 space-y-6">
          <div className="flex items-center gap-2 px-1">
            <div className="w-5 h-5 rounded-full overflow-hidden">
              <img src={LOGO_URL} alt="" className="w-full h-full object-cover scale-[1.15]" />
            </div>
            <span className="text-[11px] uppercase tracking-[0.15em] text-gray-400">
              Tu actividad
            </span>
            <button
              data-testid="history-refresh"
              onClick={load}
              className="ml-auto text-[11px] text-gray-400 hover:text-gray-700 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Actualizar
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-300">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16">
              <Sparkles className="w-7 h-7 mx-auto text-gray-200 mb-3" />
              <p className="text-sm text-gray-400">Aún no hay actividad.<br />Tu primer proyecto, compra o entrega aparecerá aquí.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map(([day, list]) => {
                const head = day === 'sin-fecha'
                  ? '—'
                  : new Date(`${day}T00:00:00`).toLocaleDateString('es-ES', {
                      weekday: 'short', day: '2-digit', month: 'long',
                    });
                return (
                  <section key={day} data-testid={`history-day-${day}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 px-1 mb-2">
                      {head}
                    </div>
                    <ul className="border border-gray-200/60 rounded-2xl bg-white/40 backdrop-blur-sm divide-y divide-gray-100">
                      {list.map((it) => {
                        const Icon = ICON_BY_TYPE[it.type] || MessageSquare;
                        const accent = ACCENT_BY_TYPE[it.type] || 'bg-gray-100 text-gray-500';
                        const positive = (it.meta?.delta ?? 0) > 0 || (it.meta?.credits ?? 0) > 0;
                        return (
                          <li
                            key={`${it.type}-${it.id}`}
                            data-testid={`history-item-${it.id}`}
                            className="flex items-start gap-3 px-4 py-3"
                          >
                            <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${accent}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <span className="text-sm text-gray-900 truncate">{it.title}</span>
                                <span className="text-[11px] text-gray-400 shrink-0 tabular-nums">
                                  {fmtDate(it.at)}
                                </span>
                              </div>
                              {(it.subtitle || it.meta?.reason) && (
                                <span className="block text-[12px] text-gray-500 mt-0.5">
                                  {it.subtitle || ''}
                                </span>
                              )}
                              {(it.meta?.delta != null || it.meta?.credits != null) && (
                                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400">
                                  {it.meta?.delta != null && (
                                    <span className={`tabular-nums ${positive ? 'text-emerald-600' : 'text-gray-700'}`}>
                                      {(it.meta.delta > 0 ? '+' : '') + it.meta.delta} créd.
                                    </span>
                                  )}
                                  {it.meta?.balance_after != null && (
                                    <span className="tabular-nums">saldo {it.meta.balance_after}</span>
                                  )}
                                  {it.meta?.amount_usd != null && (
                                    <span className="tabular-nums">${it.meta.amount_usd} USD</span>
                                  )}
                                  {it.meta?.project_size && (
                                    <span className="capitalize">{it.meta.project_size}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
